import { useState, useRef, useEffect } from 'react'
import type { AdminNewsArticle, AdminGroup } from '@/lib/api'
import { getAdminGroups } from '@/lib/api'
import { useAuth } from '@/context/AuthContext'
import AssetImageField from '@/components/AssetImageField'
import TipTapEditor from './TipTapEditor'
import EditorDetailsSidebar, { type SidebarData } from './EditorDetailsSidebar'
import SeoTab, { type SeoData } from './SeoTab'

interface FormData extends SidebarData, SeoData {
  title: string
  text: string
  image_url: string
}

function slugify(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
}

const blank: FormData = {
  title: '', slug: '', date: '', category: 'EVENT',
  text: '', image_url: '', is_featured: false,
  group_slug: null,
  seo_title: '', seo_description: '', seo_no_index: false,
  seo_focus_keyword: '', og_title: '', og_description: '', og_image_url: '',
}

function toFormData(a: AdminNewsArticle): FormData {
  return {
    title: a.title,
    slug: a.slug,
    date: a.date,
    category: a.category,
    text: a.text,
    image_url: a.image_url ?? '',
    is_featured: a.is_featured,
    group_slug: a.group_slug ?? null,
    seo_title: a.seo_title ?? '',
    seo_description: a.seo_description ?? '',
    seo_no_index: a.seo_no_index ?? false,
    seo_focus_keyword: a.seo_focus_keyword ?? '',
    og_title: a.og_title ?? '',
    og_description: a.og_description ?? '',
    og_image_url: a.og_image_url ?? '',
  }
}

export interface NewsEditorPayload {
  title: string
  slug: string
  date: string
  category: string
  text: string
  image_url: string | null
  is_featured: boolean
  is_draft: boolean
  content: string
  group_slug: string | null
  seo_title: string | null
  seo_description: string | null
  seo_no_index: boolean
  seo_focus_keyword: string | null
  og_title: string | null
  og_description: string | null
  og_image_url: string | null
}

interface NewsEditorProps {
  mode: 'create' | 'edit'
  initialData?: AdminNewsArticle
  initialContent?: string
  onSubmit: (payload: NewsEditorPayload) => Promise<void>
  onCancel: () => void
}

function nullIfEmpty(s: string): string | null {
  return s.trim() === '' ? null : s.trim()
}

export default function NewsEditor({
  mode, initialData, initialContent = '', onSubmit, onCancel,
}: NewsEditorProps) {
  const { token, isGroupManager } = useAuth()
  const [form, setFormState] = useState<FormData>(
    initialData ? toFormData(initialData) : blank
  )
  const [groups, setGroups] = useState<AdminGroup[]>([])
  const [content, setContent] = useState<string>(initialContent)
  const [saving, setSaving] = useState(false)
  const [tab, setTab] = useState<'basic' | 'seo'>('basic')
  const headlineRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (!token || isGroupManager) return
    getAdminGroups(token).then(setGroups).catch(() => {})
  }, [token, isGroupManager])

  useEffect(() => {
    const el = headlineRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [form.text, tab])

  const setField = (field: keyof FormData, value: string | boolean | null) =>
    setFormState((prev) => ({ ...prev, [field]: value }))

  const handleTitleChange = (title: string) => {
    setFormState((prev) => ({
      ...prev,
      title,
      slug: mode === 'create' ? slugify(title) : prev.slug,
    }))
  }

  const buildPayload = (isDraft: boolean): NewsEditorPayload => ({
    title: form.title,
    slug: form.slug,
    date: form.date,
    category: form.category,
    text: form.text,
    image_url: form.image_url || null,
    is_featured: form.is_featured,
    is_draft: isDraft,
    content,
    group_slug: form.group_slug,
    seo_title: nullIfEmpty(form.seo_title),
    seo_description: nullIfEmpty(form.seo_description),
    seo_no_index: form.seo_no_index,
    seo_focus_keyword: nullIfEmpty(form.seo_focus_keyword),
    og_title: nullIfEmpty(form.og_title),
    og_description: nullIfEmpty(form.og_description),
    og_image_url: nullIfEmpty(form.og_image_url),
  })

  const validate = () => {
    if (!form.title.trim()) { alert('Title wajib diisi.'); return false }
    if (!form.date) { alert('Date wajib diisi.'); return false }
    if (!form.slug.trim()) { alert('Slug wajib diisi.'); return false }
    return true
  }

  const handlePublish = async () => {
    if (!validate()) return
    setSaving(true)
    try {
      await onSubmit(buildPayload(false))
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  const handleSaveDraft = async () => {
    if (!form.title.trim()) { alert('Title wajib diisi.'); return }
    setSaving(true)
    try {
      await onSubmit(buildPayload(true))
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  const seoData: SeoData = {
    slug: form.slug,
    title: form.title,
    text: form.text,
    image_url: form.image_url,
    seo_title: form.seo_title,
    seo_description: form.seo_description,
    seo_no_index: form.seo_no_index,
    seo_focus_keyword: form.seo_focus_keyword,
    og_title: form.og_title,
    og_description: form.og_description,
    og_image_url: form.og_image_url,
  }

  const basicData: SidebarData = {
    slug: form.slug, date: form.date,
    category: form.category, is_featured: form.is_featured,
    group_slug: form.group_slug,
  }

  return (
    <div className="flex h-full">
      {/* ── Center: tabs + content ─────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Tab bar */}
        <div className="flex border-b border-zinc-800 px-8 flex-shrink-0">
          {(['basic', 'seo'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`py-3 px-4 text-sm font-medium capitalize transition-colors border-b-2 -mb-px ${
                tab === t
                  ? 'text-white border-yellow-500'
                  : 'text-zinc-500 border-transparent hover:text-zinc-300'
              }`}
            >
              {t === 'seo' ? 'SEO' : 'Basic'}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto">
          {tab === 'basic' ? (
            <div className="max-w-3xl mx-auto px-8 py-8 space-y-6">
              <div>
                <label className="block text-xs text-zinc-500 mb-2">Title</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  placeholder="Article title…"
                  className="w-full text-3xl font-bold bg-transparent text-white border-0 border-b border-zinc-800 focus:outline-none focus:border-zinc-500 pb-3 placeholder:text-zinc-700 transition-colors"
                />
              </div>

              <div>
                <p className="text-xs text-zinc-500 mb-2">Cover Image</p>
                <AssetImageField
                  value={form.image_url}
                  onChange={(url) => setField('image_url', url)}
                  feature="news"
                />
              </div>

              <div>
                <label className="block text-xs text-zinc-500 mb-2">Headline</label>
                <textarea
                  ref={headlineRef}
                  value={form.text}
                  rows={1}
                  onChange={(e) => {
                    setField('text', e.target.value)
                    const el = e.target
                    el.style.height = 'auto'
                    el.style.height = `${el.scrollHeight}px`
                  }}
                  placeholder="Short headline shown in listings…"
                  className="w-full bg-transparent text-zinc-400 text-sm border-0 border-b border-zinc-800 focus:outline-none focus:border-zinc-500 pb-2 placeholder:text-zinc-700 transition-colors resize-none overflow-hidden"
                />
              </div>

              <div>
                <p className="text-xs text-zinc-500 mb-2">Content</p>
                <TipTapEditor value={content} onChange={setContent} />
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto px-8 py-8">
              <SeoTab data={seoData} onChange={setField} />
            </div>
          )}
        </div>
      </div>

      {/* ── Right Panel: action buttons + basic fields ─────────────────── */}
      <div className="w-72 flex-shrink-0 border-l border-zinc-800 overflow-y-auto">
        <EditorDetailsSidebar
          data={basicData}
          onChange={setField}
          onPublish={handlePublish}
          onSaveDraft={handleSaveDraft}
          onCancel={onCancel}
          isLoading={saving}
          isEdit={mode === 'edit'}
          isDraft={initialData?.is_draft}
          groups={groups}
          isGroupManager={isGroupManager}
        />
      </div>
    </div>
  )
}
